import type { FormTransport } from './form-transport';
import { FORM_TRANSPORT } from './form-transport';
import { companyLooksFake } from './fake-company';
import { CLEANTALK_RESULT, type CleanTalkResult } from './cleantalk';

/**
 * Spam assessment for form submissions. Deterministic and pure, so every
 * rule here is unit tested against real examples. Nothing is ever dropped:
 * a submission judged spam is stored in quarantine (see delivery.ts) and
 * not emailed to sales or sent to Pardot; a suspect one is delivered with
 * its signals listed in the sales email.
 *
 * Why this exists: the plain HTML twin for scripts-off browsers lets a lead
 * through without any client-side check, which opened a path bots use. On
 * Sep 8, 2026 "Kristopher Mackness" arrived with the "Web Visitors Into
 * Leads" pitch, a 9-digit phone number and a French region in the Company
 * field.
 */

/**
 * The honeypot field. Rendered off-screen in every form, never filled by a
 * person; bots fill every field they find. Any value here is spam, full stop.
 *
 * The name means nothing on purpose. It used to be "website", which password
 * managers' identity fill and Safari AutoFill recognize and fill, so real
 * leads were quietly quarantined. Nothing outside this app reads the name:
 * what is stored is the `honeypot filled` signal, which does not change.
 */
export const HONEYPOT_FIELD = 'bce_hp_ref';

/**
 * The honeypot's old name, still read on the server so cached pages and bots
 * that learned it keep tripping the trap. Remove after 2026-11-01, once every
 * cached page carries the new field.
 */
export const LEGACY_HONEYPOT_FIELD = 'website';

/**
 * The honeypot value from the raw request body (before the schema strips
 * unknown keys): the new field if filled, else the legacy one.
 */
export function honeypotValue(raw: Readonly<Record<string, unknown>>): unknown {
  for (const field of [HONEYPOT_FIELD, LEGACY_HONEYPOT_FIELD]) {
    const value = raw[field];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return raw[HONEYPOT_FIELD];
}

/** True when either honeypot field holds anything a person would not type. */
export function isHoneypotFilled(raw: Readonly<Record<string, unknown>>): boolean {
  const value = honeypotValue(raw);
  return typeof value === 'string' && value.trim() !== '';
}

export const SPAM_VERDICT = {
  /** No signals. */
  clean: 'clean',
  /** Delivered, with the signals listed in the sales email. */
  suspect: 'suspect',
  /** Quarantined: stored, not emailed, not sent to Pardot. */
  spam: 'spam',
} as const;
export type SpamVerdict = (typeof SPAM_VERDICT)[keyof typeof SPAM_VERDICT];

/** Scores at or above this are quarantined. */
export const SPAM_THRESHOLD = 3;

/**
 * Phrases from the pitches that hit the sales inbox. Each is specific
 * enough that a real event planner will not trip it; "SEO" alone would.
 */
export const SPAM_PHRASES: readonly RegExp[] = [
  /generate more leads/i,
  /web visitors? into leads/i,
  /i['’]?m betting you['’]?d like/i,
  /website .{0,40}\bowner\b/i,
  /\bseo\b.{0,60}\b(services?|ranking|rank(ing)? (your|higher)|packages?|audit)\b/i,
  /\brank(ing)? (your|higher) (website|site|on google)\b/i,
  /\b(increase|boost|drive) (your )?(website |web |site )?traffic\b/i,
  /\bguest post(ing|s)?\b/i,
  /\bbacklinks?\b/i,
  /\bweb ?design (services?|agency|company)\b/i,
  /\bredesign (your )?(website|site)\b/i,
  /\b(google|facebook|instagram) ads? (management|services?|agency)\b/i,
  /\bapp development\b/i,
  /\b(crypto|bitcoin|forex)\b/i,
  /\bvirtual assistant(s)? (services?|for hire)\b/i,
  /\b(bulk|mass) (sms|email)\b/i,
  // Procurement and "supplier registration" pitches (Sep 9, 2026: a "TAQA T&D Group"
  // campaign domain asking us to join a supplier qualification process).
  /\bsupplier (qualification|registration|requirements?|onboarding)\b/i,
  /\bvendor (registration|qualification|onboarding)\b/i,
  /\b(pre-?qualification|tender) (process|documents?|invitation)\b/i,
  /\bprocurement (campaign|opportunit(y|ies)|team) .{0,40}\b(forward|suppliers?)\b/i,
];

/**
 * Requests for free goods rather than event inquiries: a person asking for
 * the backpacks or bears themselves. Real, but never a lead, and the sales
 * inbox is the wrong place for them (Sep 9, 2026, "We are the careBears").
 * Written so a planner who wants to donate supplies does not match.
 */
export const DONATION_REQUEST_PHRASES: readonly RegExp[] = [
  /\bi have no events?\b/i,
  /\bcan (i|we) (get|have|receive) (some|free|any)\b/i,
  /\b(i|we|my (kids|children|students|class|school)) (am|are|is) in need of\b/i,
  /\bplease help (me|us|my)\b/i,
  /\b(i|we) (need|could use) (some )?(help|assistance|supplies|backpacks|bears|blankets)\b/i,
  /\bi['’]?m (way )?behind\b/i,
  /\brequesting (a )?donations?\b/i,
  /\bdonat(e|ion) (to|for) (me|us|my|our (school|class|kids|children|program))\b/i,
];

/**
 * Messages from people looking for personal help, not booking an event. They
 * arrive mostly through the charity-themed ads and are never a lead (Oct 5,
 * 2026: "I am alone and widowed", company "Retired"). Written narrowly so a
 * planner describing a charity program does not match.
 */
export const PERSONAL_HARDSHIP_PHRASES: readonly RegExp[] = [
  /i(['’]?m| am) (alone|lonely|widowed|a widow(er)?|homeless|disabled|unemployed|retired)/i,
  /(pay|help with|behind on) (my|our) (rent|bills?|mortgage|utilities)/i,
  /(i|we) need (money|cash|financial (help|assistance)|a loan)/i,
  /(single (mom|mother|dad|father)|on disability|food stamps|social security check)/i,
];

/** Company field values that mean "no company". */
const NO_COMPANY = /^(n\/?a|none|no|self|personal|retired|home|individual|myself|student|private|unemployed|family|widow(ed)?|disabled)$/i;

/**
 * Consumer mailbox providers. A lead from one is common and often real, so
 * on its own it is never a signal; it only counts together with a message
 * that says nothing about an event (see {@link EVENT_DETAIL}).
 */
const PERSONAL_EMAIL = /@(gmail|googlemail|yahoo|ymail|hotmail|outlook|live|msn|aol|icloud|me|mac|comcast|att|sbcglobal|bellsouth|verizon|charter|cox|proton(mail)?)\.[a-z.]+$/i;

/** Words or numbers a planner uses when describing an event. */
const EVENT_DETAIL = /\d|\b(people|team|employees|staff|group|attendees|participants|guests|event|offsite|off-site|retreat|conference|company|corporate|meeting|holiday|party|program|activity|activities)\b/i;

/**
 * Forms where a planner describes their event, so a personal email with no
 * event detail at all is a signal. The ebook and other short forms are left out.
 */
export const EVENT_REQUEST_FORMS: ReadonlySet<string> = new Set(['contact', 'quote']);

/** A phone number with a non-North-American country code (+971, +44, …). */
const FOREIGN_PHONE = /^\s*(\+|00)\s*[2-9]/;

/** The site's own name, as bots write it when addressing "the owner". */
const OWN_DOMAIN = /bestcorporateevents(\.com|\s+com)\b/i;

const LINK = /https?:\/\/|www\.[a-z0-9-]+\.[a-z]{2,}/gi;

/**
 * Countries we deliver in. A submission from elsewhere is one signal, never
 * a verdict on its own: planners book U.S. events from abroad.
 */
const HOME_COUNTRIES: ReadonlySet<string> = new Set(['US', 'CA']);

const MIN_PHONE_DIGITS = 10;

export const SPAM_SIGNAL = {
  honeypot: 'honeypot filled',
  phrases: 'sales-pitch phrases',
  links: 'links in the message',
  manyLinks: 'three or more links',
  shortPhone: 'phone number under 10 digits',
  unverified: 'no bot check',
  abroad: 'IP outside the U.S. and Canada',
  ownDomain: 'addresses the site by name',
  donationRequest: 'asks for free goods, not an event',
  foreignPhone: 'phone number with a foreign country code',
  cleantalk: 'CleanTalk denied',
  personalHardship: 'asks for personal help, not an event',
  personalNoDetail: 'personal email and no event details',
  personalNoDetailFakeCompany: 'personal email, empty message and no real company',
} as const;
export type SpamSignal = (typeof SPAM_SIGNAL)[keyof typeof SPAM_SIGNAL];

/** Weight per signal. Documented here rather than spread through the code. */
const WEIGHT: Readonly<Record<SpamSignal, number>> = {
  [SPAM_SIGNAL.honeypot]: SPAM_THRESHOLD,
  [SPAM_SIGNAL.phrases]: 2,
  [SPAM_SIGNAL.links]: 1,
  [SPAM_SIGNAL.manyLinks]: 1,
  [SPAM_SIGNAL.shortPhone]: 1,
  [SPAM_SIGNAL.unverified]: 1,
  [SPAM_SIGNAL.abroad]: 1,
  [SPAM_SIGNAL.ownDomain]: 1,
  // Never a lead, so it quarantines on its own; the message is still stored.
  [SPAM_SIGNAL.donationRequest]: SPAM_THRESHOLD,
  [SPAM_SIGNAL.foreignPhone]: 1,
  // CleanTalk read the email, IP and message against its database; a denial
  // quarantines on its own. Stored, never deleted, so a false positive is recoverable.
  [SPAM_SIGNAL.cleantalk]: SPAM_THRESHOLD,
  // Never a lead; quarantined so it is reviewed, not sent to sales or counted by Ads.
  [SPAM_SIGNAL.personalHardship]: SPAM_THRESHOLD,
  // A warning, never a hold on its own (Thomas, Oct 5 2026): real buyers were
  // held for it. Adds to a hold only alongside real spam signs. Mirrors bcecloud.
  [SPAM_SIGNAL.personalNoDetail]: 1,
  // All three together (Thomas, Oct 8 2026): a phone number, "babysiter", "VA",
  // an apartment number or abuse as the company, from a personal mailbox with an
  // empty message. Enough to hold. Mirrors bcecloud #798.
  [SPAM_SIGNAL.personalNoDetailFakeCompany]: SPAM_THRESHOLD,
};

export interface SpamInput {
  readonly fields: Readonly<Record<string, unknown>>;
  /** The raw honeypot value, read before the schema strips unknown keys. */
  readonly honeypot: unknown;
  readonly transport: FormTransport;
  /** ISO 3166-1 alpha-2 from Cloudflare's cf-ipcountry header, if present. */
  readonly country: string | undefined;
  /** CleanTalk's verdict, when the check ran (see cleantalk.ts). */
  readonly cleantalk?: CleanTalkResult;
  /** The form posted to; the event-detail check only runs for {@link EVENT_REQUEST_FORMS}. */
  readonly formKey?: string;
}

export interface SpamAssessment {
  readonly verdict: SpamVerdict;
  readonly score: number;
  readonly signals: readonly SpamSignal[];
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Every text field a bot could have written into, joined for scanning. */
function prose(fields: Readonly<Record<string, unknown>>): string {
  return ['message', 'company', 'firstName', 'lastName', 'eventCity']
    .map((key) => text(fields[key]))
    .join('\n');
}

/**
 * The Oct 8 2026 hold: an event-request form from a personal mailbox, with an
 * empty message and a company that is no organization ({@link companyLooksFake}).
 * Exported for the bcecloud path, which hands spam scoring to bcecloud but must
 * still withhold the Ads conversion for a lead bcecloud will hold.
 */
export function isPersonalNoDetailFakeCompany(formKey: string | undefined, fields: Readonly<Record<string, unknown>>): boolean {
  return (
    formKey !== undefined &&
    EVENT_REQUEST_FORMS.has(formKey) &&
    PERSONAL_EMAIL.test(text(fields['email']).trim()) &&
    text(fields['message']).trim() === '' &&
    companyLooksFake(text(fields['company']))
  );
}

export function assessSpam(input: SpamInput): SpamAssessment {
  const signals: SpamSignal[] = [];
  if (text(input.honeypot).trim() !== '') signals.push(SPAM_SIGNAL.honeypot);

  const body = prose(input.fields);
  if (SPAM_PHRASES.some((pattern) => pattern.test(body))) signals.push(SPAM_SIGNAL.phrases);

  const links = body.match(LINK)?.length ?? 0;
  if (links >= 1) signals.push(SPAM_SIGNAL.links);
  if (links >= 3) signals.push(SPAM_SIGNAL.manyLinks);

  const phoneDigits = text(input.fields['phone']).replace(/\D/g, '');
  if (phoneDigits.length > 0 && phoneDigits.length < MIN_PHONE_DIGITS) signals.push(SPAM_SIGNAL.shortPhone);
  if (FOREIGN_PHONE.test(text(input.fields['phone']))) signals.push(SPAM_SIGNAL.foreignPhone);

  if (DONATION_REQUEST_PHRASES.some((pattern) => pattern.test(text(input.fields['message'])))) {
    signals.push(SPAM_SIGNAL.donationRequest);
  }
  if (input.cleantalk === CLEANTALK_RESULT.denied) signals.push(SPAM_SIGNAL.cleantalk);

  const message = text(input.fields['message']);
  if (PERSONAL_HARDSHIP_PHRASES.some((pattern) => pattern.test(message)) || NO_COMPANY.test(text(input.fields['company']).trim())) {
    signals.push(SPAM_SIGNAL.personalHardship);
  }
  if (
    input.formKey !== undefined &&
    EVENT_REQUEST_FORMS.has(input.formKey) &&
    PERSONAL_EMAIL.test(text(input.fields['email']).trim()) &&
    !EVENT_DETAIL.test(message)
  ) {
    signals.push(SPAM_SIGNAL.personalNoDetail);
  }
  if (isPersonalNoDetailFakeCompany(input.formKey, input.fields)) signals.push(SPAM_SIGNAL.personalNoDetailFakeCompany);

  if (input.transport === FORM_TRANSPORT.html) {
    signals.push(SPAM_SIGNAL.unverified);
  }

  if (input.country && !HOME_COUNTRIES.has(input.country.toUpperCase())) signals.push(SPAM_SIGNAL.abroad);

  if (OWN_DOMAIN.test(text(input.fields['message']))) signals.push(SPAM_SIGNAL.ownDomain);

  const score = signals.reduce((sum, signal) => sum + WEIGHT[signal], 0);
  const verdict =
    score >= SPAM_THRESHOLD ? SPAM_VERDICT.spam : score > 0 ? SPAM_VERDICT.suspect : SPAM_VERDICT.clean;
  return { verdict, score, signals };
}
